import { createMachine, assign } from 'xstate5'
import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import { trackQuizPageViewed } from 'libs/events'
import {
  LOGO_ANIMATION_DELAY,
  TOTAL_TIME_IN_LOGO,
  INTERSTITIAL_STEP_TIME,
} from './steps/PersonalizedWayfinding/constants'
import type { Context, Events } from './types'
import {
  getCategoryInterestsUrlParam,
  getSubcatInterestsUrlParam,
  updateQueryParam,
  getCategoryInterests,
  noSubcatRefinedCategories,
  userShouldSkipToMotivationRefinement,
  userShouldSkipToSubcatInterests,
  allCategoriesHaveBeenSubcatRefined,
  shouldShowSpringboardInterstitial,
  // start pen-5972_v1_quiz_time_reordering
  shouldShowTimeQuestionFirstExp,
  userShouldSkipToTimeQuestionFirstExp,
  // end pen-5972_v1_quiz_time_reordering
} from './utils'

export const STEP_NAME = {
  SPRINGBOARD_INTERSTITIAL: 'springboard-interstitial', // pen-5453_v1_hp_carousel_to_quiz
  PRIMER_QUESTION: 'primer-question',
  MOTIVATION_REFINEMENT: 'motivation-category-refinement',
  TIME_QUESTION: 'time-interstitial',
  TIME_QUESTION_ANYTIME_ANYWHERE: 'time-interstitial-anytime-anywhere',
  SUBCATEGORY_QUESTION: 'subcategory-question',
  FULL_ACCESS_INTERSTITIAL: 'full-access-interstitial',
} as const

const LOADING = 'loading'
const TRANSIENT = 'transient'
const INTERACTIVE = 'interactive'
const INTERSTITIAL = 'interstitial'
export const TAGS = {
  Loading: LOADING,
  Transient: TRANSIENT,
  Interactive: INTERACTIVE,
  Interstitial: INTERSTITIAL,
} as const

export const baseContext = {
  stepType: TAGS.Interactive,
  userHasSkipQ1Param: false,
  step: '1',
  stepHistory: ['1'],
  stateHistory: ['categoryInterests'],
  stepName: STEP_NAME.PRIMER_QUESTION,
  allCategories: [],
  allSubcategories: [],
  categoryInterestsCSV: undefined,
  springboardItemType: null,
  springboardItem: null,
  totalSteps: 4,
  userHasSkipMotivationRefinementParam: false,
  isBogoOfferEligible: false,
  isTimeQuestionFirstExpVariant: false, // pen-5972_v1_quiz_time_reordering
}

export const quizMachine = createMachine(
  {
    id: 'quizMachine',
    types: {} as {
      context: Context
      events: Events
      input: Partial<Context>
    },
    context: ({ input }) => ({
      ...baseContext,
      ...input,
    }),
    on: {
      updateStepHistory: {
        actions: assign({
          step: ({ event }) => event.step,
          stepHistory: ({ event }) => event.stepHistory,
          stateHistory: ({ event }) => event.stateHistory || [],
        }),
      },
    },
    initial: 'initialize',
    states: {
      initialize: {
        description: 'This is the mandatory entry state of the quiz.',
        tags: [LOADING],
        entry: ['trackQuizPageViewed', 'clientUpdateUrlToContext'],
        always: [
          {
            target: 'subcatsInitialization',
            guard: 'userShouldSkipToSubcatInterests',
            actions: 'setSubcatInterestsInitialHistory',
          },
          // pen-5972_v1_quiz_time_reordering
          // should go to learningTime before motivationRefinementPage
          // if user already saw categoryInterests in quiz on HP
          {
            target: 'learningTime',
            guard: 'userShouldSkipToTimeQuestionFirstExp',
            actions: 'setLearningTimeReorderingInitialHistory',
          },
          {
            target: 'motivationRefinementPage',
            guard: 'userShouldSkipToMotivationRefinement',
            actions: 'setMotivationRefinementInitialHistory',
          },
          {
            // pen-5453_v1_hp_carousel_to_quiz
            target: 'springboardInterstitial',
            guard: 'shouldShowSpringboardInterstitial',
            actions: 'setSpringboardInterstitialInitialHistory',
          },
          {
            target: 'categoryInterests',
          },
        ],
      },

      // pen-5453_v1_hp_carousel_to_quiz
      springboardInterstitial: {
        entry: 'setSpringboardInterstitialData',
        description: 'Springboard interstitial step',
        tags: [INTERSTITIAL],
        on: {
          next: [
            {
              target: 'categoryInterests',
            },
          ],
        },
      },
      categoryInterests: {
        entry: 'setMotivationInteractiveData',
        exit: 'initializeSubcatRefinements',
        description: 'Interactive step "What brings you to MasterClass today"',
        tags: [INTERACTIVE],

        on: {
          back: [
            {
              // pen-5453_v1_hp_carousel_to_quiz
              target: 'springboardInterstitial',
              guard: 'shouldShowSpringboardInterstitial',
            },
            {
              guard: 'userDoesNotHaveSkipQ1Param',
              target: 'homePage',
            },
          ],
          next: [
            // pen-5972_v1_quiz_time_reordering
            // should go to learningTime before motivationRefinementPage
            {
              target: 'learningTime',
              guard: 'shouldShowTimeQuestionFirstExp',
              // actions: 'setLearningTimeReorderingInitialHistory',
            },
            {
              target: 'motivationRefinementPage',
            },
          ],
        },
      },

      fullAccess: {
        entry: 'onFullAccessEntry',
        description: 'Interstitial step "Did you know?"',
        tags: [INTERSTITIAL],

        on: {
          next: [{ target: 'bogoOffer' }],

          back: [
            {
              target: 'subcatInterests',
              actions: ['removeLastCategoryFromRefined'],
            },
          ],
        },
      },

      bogoOfferInterstitial: {
        entry: 'setBogoOfferInterstitialStepData',
        description: 'Display BOGO offer promo',
        tags: [INTERSTITIAL],
        on: {
          next: [
            // pen-5972_v1_quiz_time_reordering
            // replicated from ownPace next logic (control flow)
            // we don't want to go to learningTime again
            {
              guard: 'shouldShowTimeQuestionFirstExp',
              target: 'completeQuiz',
            },
            {
              target: 'learningTime',
            },
          ],
          back: [
            {
              target: 'fullAccess',
            },
          ],
        },
      },

      learningTime: {
        entry: 'setLearningTimeStepData',
        description:
          'Interactive step "How much time do you want to spend learning"',
        tags: [INTERACTIVE],
        on: {
          next: {
            target: 'ownPace',
            actions: ['setTimeSpent'],
          },
          back: [
            // pen-5972_v1_quiz_time_reordering
            // replicated from motivationRefinementPage back logic (control flow)
            {
              guard: 'shouldShowTimeQuestionFirstExp',
              target: 'categoryInterests',
            },
            {
              guard: 'isBogoOfferEnabled',
              target: 'bogoOfferInterstitial',
            },
            {
              target: 'fullAccess',
            },
          ],
        },
      },

      ownPace: {
        entry: 'setOwnPaceInterstitialData',
        description: 'Interstitial step "Did you know?"',
        tags: [INTERSTITIAL],
        on: {
          next: [
            // pen-5972_v1_quiz_time_reordering
            // replicated from categoryInterests next logic (control flow)
            {
              guard: 'shouldShowTimeQuestionFirstExp',
              target: 'motivationRefinementPage',
            },
            {
              target: 'completeQuiz',
            },
          ],
          back: {
            target: 'learningTime',
          },
        },
      },

      completeQuiz: {
        entry: 'prepareCompletion',
        description:
          'This state prepares the quiz exit by showing a custom loading page',
        initial: 'unanimated',
        states: {
          unanimated: {
            after: { LOGO_ANIMATION_DELAY: { target: 'animating' } },
          },
          animating: {
            after: { TOTAL_TIME_IN_LOGO: { target: 'animated' } },
          },
          animated: {
            after: {
              INTERSTITIAL_WITH_LOGO_DELAY: {
                target: 'delayed',
              },
            },
          },
          delayed: {
            after: {
              LOGO_ANIMATION_DELAY: { target: '#quizMachine.completed' },
            },
          },
        },
      },

      completed: {
        entry: 'quizCompleted',
        description: 'This state redirects to the class recs results',
        type: 'final',
      },

      subcatInterests: {
        description:
          'Interactive step: Select subcategory interests. Repeats until all necessary categories have been refined with subcat interests.',
        tags: [INTERACTIVE],
        entry: ['onSubcatInterestsEntry'],

        on: {
          next: [
            {
              target: 'fullAccess',
              guard: 'allCategoriesHaveBeenSubcatRefined',
              actions: ['updateSubcatRefinements'],
            },
            {
              target: 'subcatInterests',
              reenter: true,
              actions: 'updateSubcatRefinements',
            },
          ],

          back: [
            {
              target: 'motivationRefinementPage',
              guard: 'noCategoriesHaveBeenSubcatRefined',
            },
            {
              target: 'subcatInterests',
              actions: 'removeLastCategoryFromRefined',
            },
          ],
        },
      },

      bogoOffer: {
        description:
          '**Transient state**: handle state transitions when bogo offer is on',
        tags: [TRANSIENT],
        always: [
          {
            target: 'bogoOfferInterstitial',
            guard: 'isBogoOfferEnabled',
          },
          // pen-5972_v1_quiz_time_reordering
          // replicated from ownPace next logic (control flow)
          // we don't want to go to learningTime again
          {
            guard: 'shouldShowTimeQuestionFirstExp',
            target: 'completeQuiz',
          },
          {
            target: 'learningTime',
          },
        ],
      },

      homePage: {
        entry: 'goToHomePage',
        description: 'This state redirects to the homepage after going back',
        type: 'final',
        tags: [TRANSIENT],
      },

      motivationRefinementPage: {
        description:
          'Motivation refinement (select specific category interests)',
        entry: 'onMotivationRefinementEntry',
        exit: [
          'initializeSubcatRefinements',
          'setMotivationRefinementPageViewed',
        ],
        tags: [INTERACTIVE],

        on: {
          back: [
            // pen-5972_v1_quiz_time_reordering
            {
              guard: 'shouldShowTimeQuestionFirstExp',
              target: 'ownPace',
            },
            {
              target: 'categoryInterests',
            },
          ],
          next: {
            target: 'subcatsInitialization',
          },
        },
      },

      subcatsInitialization: {
        description:
          '**Transient state**: Entry state for subcat step. Sets up machine to handle category subcat refinements',

        tags: [TRANSIENT],
        exit: 'initializeSubcatRefinements',

        always: ['subcatInterests'],
      },
    },
  },
  {
    actions: {
      // no-op actions to satisfy TypeScript; to implement in `customActions` (Quiz.tsx)
      quizCompleted: () => null,
      goToHomePage: () => null,
      prepareCompletion: () => null,
      // end no-op actions

      trackQuizPageViewed: ({ context }) =>
        trackQuizPageViewed({
          promo_banner_visible: !!context.isBogoOfferEligible,
        }),
      // pen-5453_v1_hp_carousel_to_quiz
      setSpringboardInterstitialData: assign({
        stepType: INTERSTITIAL,
        stepName: STEP_NAME.SPRINGBOARD_INTERSTITIAL,
        interstitialScreen: 'springboard interstitial',
      }),
      setSpringboardInterstitialInitialHistory: assign({
        step: '0',
        stepHistory: ['0'],
        stateHistory: ['springboardInterstitial'],
      }),
      // end pen-5453_v1_hp_carousel_to_quiz
      setMotivationInteractiveData: assign({
        stepType: INTERACTIVE,
        stepName: STEP_NAME.PRIMER_QUESTION,
        interstitialScreen: undefined,
      }),
      onMotivationRefinementEntry: assign({
        stepType: INTERACTIVE,
        stepName: STEP_NAME.MOTIVATION_REFINEMENT,
        interstitialScreen: undefined,
      }),
      onSubcatInterestsEntry: assign({
        stepType: INTERACTIVE,
        stepName: STEP_NAME.SUBCATEGORY_QUESTION,
        interstitialScreen: undefined,
      }),
      setSubcatInterestsInitialHistory: assign(({ context }) => {
        if (context.isTimeQuestionFirstExpVariant) {
          return {
            step: '4',
            stepHistory: ['1', '2', '2.1', '3', '4'],
            stateHistory: [
              'categoryInterests',
              'learningTime',
              'ownPace',
              'motivationRefinementPage',
              'subcatInterests',
            ],
          }
        }
        return {
          step: '3',
          stepHistory: ['1', '2', '3'],
          stateHistory: [
            'categoryInterests',
            'motivationRefinementPage',
            'subcatInterests',
          ],
        }
      }),
      setMotivationRefinementInitialHistory: assign({
        step: '2',
        stepHistory: ['1', '2'],
        stateHistory: ['categoryInterests', 'motivationRefinementPage'],
      }),
      // pen-5972_v1_quiz_time_reordering
      setLearningTimeReorderingInitialHistory: assign({
        step: '2',
        stepHistory: ['1', '2'],
        stateHistory: ['categoryInterests', 'learningTime'],
      }),
      // end pen-5972_v1_quiz_time_reordering
      onFullAccessEntry: assign({
        stepType: INTERSTITIAL,
        interstitialScreen: 'full access',
        stepName: STEP_NAME.FULL_ACCESS_INTERSTITIAL,
      }),
      setLearningTimeStepData: assign({
        stepType: INTERACTIVE,
        stepName: STEP_NAME.TIME_QUESTION,
        interstitialScreen: undefined,
      }),
      setBogoOfferInterstitialStepData: assign({
        stepType: INTERSTITIAL,
        interstitialScreen: 'bogo explainer',
      }),
      setOwnPaceInterstitialData: assign({
        stepType: INTERSTITIAL,
        stepName: STEP_NAME.TIME_QUESTION_ANYTIME_ANYWHERE,
        interstitialScreen: 'learning devices',
      }),
      setTimeSpent: assign(({ event }) => ({
        timeSpent:
          event.type === 'next' &&
          event.params?.machineEventType === 'time_spent'
            ? event.params.timeSpent
            : undefined,
      })),
      clientUpdateUrlToContext: assign({
        categoryInterestsCSV: ({ context }) =>
          getCategoryInterestsUrlParam() ?? context.categoryInterestsCSV,
      }),
      setMotivationRefinementPageViewed: assign(() => {
        // user came from motivation refinement screen
        // so we mark it as viewed in the URL
        updateQueryParam(
          QUIZ_QUERY_PARAMS.MOTIVATION_REFINEMENT_VIEWED,
          QUIZ_QUERY_PARAMS.WAS_VIEWED,
        )
        return { userHasSkipMotivationRefinementParam: true }
      }),
      initializeSubcatRefinements: assign(({ context }) => {
        const categoryInterestsCSV =
          getCategoryInterestsUrlParam() ?? context.categoryInterestsCSV
        const allCatsToRefineSubcats = getCategoryInterests(
          categoryInterestsCSV || '',
          context.allCategories,
        )
        // at any moment, the subcats selected should reflect the categories selected.
        // e.g. if a user specifies interest in category 1 then selects subcats within it
        // then they go back, remove category 1 interest and select new subcats
        // we should NOT have any subcats from the no-longer selected category 1
        const validSubcats = new Set(
          context.allSubcategories
            .filter(
              (s) =>
                !!s.parentId && allCatsToRefineSubcats.includes(s.parentId),
            )
            .map((s) => s.id),
        )

        const allSCs =
          getSubcatInterestsUrlParam() || context.subcatInterests || []
        const subcatInterests = allSCs.filter((sc) => validSubcats.has(sc))

        return {
          categoryInterestsCSV,
          catsToRefineSubcats: allCatsToRefineSubcats,
          subcatInterests,
        }
      }),
      updateSubcatRefinements: assign({
        subcatInterests: ({ context, event }) => {
          if (
            event.type === 'next' &&
            event.params?.machineEventType === 'subcat_refinement'
          ) {
            const { params } = event
            const { allSubcategories } = context

            const scIds = new Set(
              allSubcategories
                .filter((s) => s.parentId === params.categoryIdRefined)
                .map((s) => s.id),
            )

            // for users who go back and update;
            // the "previous" subcat selections should NOT include
            // any subcats that are in the current category. because
            // the new subcat interests (for the current category)
            // will override all those previous selections
            const currentCatSubcats = (scId: number) => !scIds.has(scId)
            const prev = (context.subcatInterests || []).filter(
              currentCatSubcats,
            )
            const newSubcatInterests = [...prev, ...params.subcatInterests]
            updateQueryParam(
              QUIZ_QUERY_PARAMS.SELECTED_SUBCATEGORIES,
              newSubcatInterests.join(','),
            )
            return newSubcatInterests
          }
          return undefined
        },
        catsToRefineSubcats: ({ context, event }) => {
          if (event.type !== 'next') {
            return undefined
          }
          if (event.params?.machineEventType !== 'subcat_refinement') {
            return undefined
          }
          return context.catsToRefineSubcats?.filter(
            (catId) =>
              catId !==
              (event.params as { categoryIdRefined: number }).categoryIdRefined,
          )
        },
      }),
      removeLastCategoryFromRefined: assign({
        catsToRefineSubcats: ({ context }) => {
          // basically we want to re-add the category id that was removed
          // start with the original sorted order
          const allCats = getCategoryInterests(
            context.categoryInterestsCSV || '',
            context.allCategories,
          )
          // get already refined categories
          const unrefinedCats = context.catsToRefineSubcats || []
          const refinedCats = allCats.filter(
            (catId) => !unrefinedCats?.includes(catId),
          )
          // add last refined cat to front of unrefined list
          const lastRefinedCat = refinedCats[refinedCats.length - 1]
          return [lastRefinedCat, ...unrefinedCats]
        },
      }),
    },
    delays: {
      LOGO_ANIMATION_DELAY,
      TOTAL_TIME_IN_LOGO,
      INTERSTITIAL_WITH_LOGO_DELAY:
        INTERSTITIAL_STEP_TIME + LOGO_ANIMATION_DELAY,
    },
    guards: {
      allCategoriesHaveBeenSubcatRefined: ({ context, event }) => {
        if (event.type !== 'next') return false
        return allCategoriesHaveBeenSubcatRefined(context, event)
      },
      noCategoriesHaveBeenSubcatRefined: ({ context }) =>
        noSubcatRefinedCategories(context),
      isBogoOfferEnabled: ({ context }) => !!context.isBogoOfferEligible,
      // pen-5453_v1_hp_carousel_to_quiz
      shouldShowSpringboardInterstitial: ({ context }) =>
        shouldShowSpringboardInterstitial(context),
      userShouldSkipToSubcatInterests: ({ context }) =>
        userShouldSkipToSubcatInterests(context),
      userShouldSkipToMotivationRefinement: ({ context }) =>
        userShouldSkipToMotivationRefinement(context),
      userDoesNotHaveSkipQ1Param: ({ context }) => !context.userHasSkipQ1Param,
      // start pen-5972_v1_quiz_time_reordering
      shouldShowTimeQuestionFirstExp: ({ context }) =>
        shouldShowTimeQuestionFirstExp(context),
      userShouldSkipToTimeQuestionFirstExp: ({ context }) =>
        userShouldSkipToTimeQuestionFirstExp(context),
      // end pen-5972_v1_quiz_time_reordering
    },
  },
)
